'use client';

import { useEffect } from 'react';

import { Text } from '@/design-system';
import AlertModalV2 from '../../Member/components/AlertModalV2';
import { ReactNativeGtm } from '../../utils/gtm/react-native/ReactNativeGtm';
import { SVG } from '../SVG';
import UImage from '../UImage';
import {
  useWebToAppInstallModal,
  WebToAppModalStep,
} from './useWebToAppInstallModal';
import WebToAppCouponIssuedModal from './WebToAppCouponIssuedModal';

const BACKGROUND_IMAGE_URL = `${process.env.NEXT_PUBLIC_S3_IMAGE_URL}/assets/app-install-promotion/app_install_backgound.png`;
const COUPON_IMAGE_URL = `${process.env.NEXT_PUBLIC_S3_IMAGE_URL}/assets/app-install-promotion/app_install_coupon.png`;

/**
 * 웹→앱 설치 유도 모달 (1단계: 설치 유도 + 쿠폰 받기)
 *
 * 기존 앱 설치 띠배너를 대체한다. 앱 전용 쿠폰을 안내하고 "쿠폰 받기" 클릭 시
 * 발급 완료 모달(WebToAppCouponIssuedModal)로 전환한다.
 * 노출 조건·단계 전환·재노출 쿨다운·다른 자동 노출 모달과의 중첩 방지는 useWebToAppInstallModal에서 담당한다.
 */
const WebToAppInstallModal = () => {
  const {
    isOpen,
    step,
    issuedCoupon,
    handleClose,
    handleNeverShowForSevenDays,
    handleClickIssueCoupon,
    handleClickUseInApp,
  } = useWebToAppInstallModal();

  useEffect(() => {
    if (!isOpen) return;

    new ReactNativeGtm().view_app_install_banner();
  }, [isOpen]);

  if (!isOpen) return null;

  if (step === WebToAppModalStep.COUPON_ISSUED) {
    return (
      <WebToAppCouponIssuedModal
        coupon={issuedCoupon}
        onClickUseInApp={handleClickUseInApp}
        onClose={handleClose}
      />
    );
  }

  return (
    <AlertModalV2
      open={isOpen}
      onClose={handleClose}
      hasButtonGroup={false}
      hasCloseButton={false}
      zeroPadding
      StyledDialog={{ maxWidth: '343px', backgroundColor: 'transparent' }}
      content={
        <div className="relative flex w-full flex-col items-center justify-center gap-[8px] overflow-hidden rounded-[16px] bg-white px-[32px] pb-[24px] pt-[40px]">
          <UImage
            src={BACKGROUND_IMAGE_URL}
            alt=""
            width={288}
            height={360}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <button
            onClick={handleClose}
            aria-label="닫기"
            className="absolute right-0 top-0 flex size-[56px] items-center justify-center"
            type="button"
          >
            <SVG.Close className="h-[24px] w-[24px] stroke-gray-900 stroke-[1.8]" />
          </button>
          <Text
            variant="h2-bold"
            as="p"
            className="relative whitespace-pre-line text-center text-gray-900"
          >
            {'APP설치하고\n5,000원 쿠폰 받기'}
          </Text>
          <div className="relative flex w-full flex-col items-center gap-[12px]">
            <UImage
              src={COUPON_IMAGE_URL}
              alt="APP 전용 5,000원 쿠폰"
              width={246}
              height={162}
            />
            <button
              onClick={handleClickIssueCoupon}
              className="flex h-[48px] w-full items-center justify-center rounded-[10px] bg-soil-brown-700"
              type="button"
            >
              <Text variant="h5-bold-100" as="span" className="text-white">
                쿠폰 받기
              </Text>
            </button>
            <button
              onClick={handleNeverShowForSevenDays}
              className="flex items-center"
              type="button"
            >
              <Text
                variant="h7-regular-100"
                as="span"
                className="text-gray-600"
              >
                7일 동안 보지 않기
              </Text>
            </button>
          </div>
        </div>
      }
    />
  );
};

export default WebToAppInstallModal;
